import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useUpdateOrganizationUser } from "src/api/taktile/mutations";
import { organizationUsersApi, rolesApi } from "src/api/taktileApi";
import { RoleOperation, SimpleRoleType } from "src/clients/taktile-api";
import { useWorkspaceContext } from "src/router/routerContextHooks";

const userWorkspaceRolesKey = (
  orgId: string,
  workspaceId: string,
  userId: string,
) => ["userWorkspaceRoles", orgId, workspaceId, userId];

type UseUserWorkspaceRolesArgs = {
  userId: string;
};

export const useUserWorkspaceRoles = ({
  userId,
}: UseUserWorkspaceRolesArgs) => {
  const { orgId, workspace } = useWorkspaceContext();
  return useQuery({
    queryKey: userWorkspaceRolesKey(orgId, workspace.id, userId),
    queryFn: async () => {
      const response =
        await organizationUsersApi.getUserRolesApiV1OrganizationsOrgIdUsersUserIdRolesGet(
          orgId,
          userId,
          workspace.id,
        );

      return response.data;
    },
    enabled: !!orgId && !!workspace.id && !!userId,
  });
};

export type UserPermissionsUpdate = {
  operations: RoleOperation[];
  orgRole?: SimpleRoleType;
};

export const useSaveUserPermissions = ({
  userId,
}: UseUserWorkspaceRolesArgs) => {
  const { orgId, workspace } = useWorkspaceContext();
  const queryClient = useQueryClient();
  const updateOrganizationUser = useUpdateOrganizationUser(orgId, userId);

  return useMutation({
    mutationFn: async ({ operations, orgRole }: UserPermissionsUpdate) => {
      if (orgRole) {
        await updateOrganizationUser.mutateAsync({ role: orgRole });
      }
      if (operations.length > 0) {
        await rolesApi.applyRoleOperationsApiV1RolesUserIdOperationsPost(
          userId,
          { operations },
        );
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: userWorkspaceRolesKey(orgId, workspace.id, userId),
      });
      await queryClient.invalidateQueries({ queryKey: [userId] });
    },
  });
};
